import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import "./UpdateProfile.css";

import { BsCloudArrowUp } from "react-icons/bs";

import logoImage from "../../assets/logo_trucktronix.png";
import iconEmployee from "../../assets/icon_employee.jpg";
import LoadingSpinner from "../../component/LoadingSpinner";

import {
  serviceFetchAdminDetails,
  serviceUpdateAdminDetails,
} from "../../api/serviceFleetMaster.js";

import {
  serviceUploadPhoto,
  serviceDeletePhoto,
} from "../../api/servicePhotoApi.js";

import { getImageUrl } from "../../utils/helper";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

// ============================================================
// HELPERS
// ============================================================

const normalize = (value) => (value == null ? "" : String(value).trim());

const isPhoneValid = (value) => {
  const raw = String(value || "");
  const digits = raw.replace(/\D/g, "");

  const looksOk = /^[0-9+\-()\s]*$/.test(raw);

  return looksOk && digits.length >= 8 && digits.length <= 15;
};

const isImageFile = (file) => {
  if (file?.type?.startsWith("image/")) {
    return true;
  }

  return /\.(jpg|jpeg|png|webp|heic|heif|avif)$/i.test(file?.name || "");
};

const makeProfileFilename = (email, id) => {
  const safeValue = String(email || id || "admin")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "_");

  return [
    safeValue,
    "profile",
    Date.now(),
    Math.random().toString(36).slice(2, 8),
  ].join("-");
};

const isManagedPhoto = (url) => {
  if (!url) {
    return false;
  }

  return String(url).startsWith("/photo/") || String(url).includes("/photo/");
};

// ============================================================
// COMPONENT
// ============================================================

const UpdateProfile = () => {
  const navigate = useNavigate();

  const adminId = sessionStorage.getItem("id") || "";

  const [details, setDetails] = useState(null);

  const [baselineDetails, setBaselineDetails] = useState(null);

  /*
   * Original image returned by the API.
   *
   * Keep this independently so it can be deleted
   * only after the profile update succeeds.
   */
  const [originalImageUrl, setOriginalImageUrl] = useState("");

  /*
   * Newly selected File.
   */
  const [image, setImage] = useState(null);

  /*
   * Browser-local preview URL.
   */
  const [previewUrl, setPreviewUrl] = useState("");

  /*
   * Existing photo explicitly marked for removal.
   */
  const [removeExistingImage, setRemoveExistingImage] = useState(false);

  const [loading, setLoading] = useState(true);

  const [submitting, setSubmitting] = useState(false);

  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const busy = loading || submitting || uploadingPhoto;

  // ==========================================================
  // DIRTY STATE
  // ==========================================================

  const isDirty = useMemo(() => {
    if (!details || !baselineDetails) {
      return false;
    }

    /*
     * Selected replacement image.
     */
    if (image) {
      return true;
    }

    /*
     * Existing image removed.
     */
    if (removeExistingImage) {
      return true;
    }

    return (
      normalize(details?.admin?.name) !==
        normalize(baselineDetails?.admin?.name) ||
      normalize(details?.admin?.phone) !==
        normalize(baselineDetails?.admin?.phone)
    );
  }, [details, baselineDetails, image, removeExistingImage]);

  const canSave = useMemo(() => {
    if (!details || !isDirty || busy) {
      return false;
    }

    if (!normalize(details?.admin?.name)) {
      return false;
    }

    if (!normalize(details?.admin?.email)) {
      return false;
    }

    if (!isPhoneValid(details?.admin?.phone)) {
      return false;
    }

    return true;
  }, [details, isDirty, busy]);

  // ==========================================================
  // LOAD PROFILE
  // ==========================================================

  useEffect(() => {
    let cancelled = false;

    const fetchProfile = async () => {
      try {
        setLoading(true);

        const data = await serviceFetchAdminDetails();

        if (cancelled) {
          return;
        }

        if (!data?.admin) {
          throw new Error("Admin profile was not found.");
        }

        setDetails(data);

        /*
         * Create a separate snapshot.
         */
        setBaselineDetails(structuredClone(data));

        const currentImage = data?.admin?.imageUrl || "";

        setOriginalImageUrl(currentImage);

        setImage(null);

        setRemoveExistingImage(false);
      } catch (error) {
        console.error("Error fetching admin profile:", error);

        if (!cancelled) {
          toast.error(error?.message || "Unable to load profile.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchProfile();

    return () => {
      cancelled = true;
    };
  }, []);

  // ==========================================================
  // LOCAL IMAGE PREVIEW
  // ==========================================================

  useEffect(() => {
    if (!image) {
      setPreviewUrl("");
      return;
    }

    const objectUrl = URL.createObjectURL(image);

    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [image]);

  // ==========================================================
  // ADMIN FIELD CHANGE
  // ==========================================================

  const updateAdminField = (field, value) => {
    setDetails((current) => ({
      ...current,

      admin: {
        ...current.admin,
        [field]: value,
      },
    }));
  };

  // ==========================================================
  // PHOTO SELECTION
  // ==========================================================

  const handleImageChoose = (event) => {
    const file = event.target.files?.[0];

    /*
     * Allow selecting the same file again.
     */
    event.target.value = "";

    if (!file) {
      return;
    }

    if (!isImageFile(file)) {
      toast.error("Please choose a valid image file.");

      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error("Profile image must be 5MB or smaller.");

      return;
    }

    setImage(file);

    /*
     * Choosing another image cancels a previous
     * Remove Photo action.
     */
    setRemoveExistingImage(false);
  };

  // ==========================================================
  // REMOVE PHOTO
  // ==========================================================

  const handleRemovePhoto = () => {
    if (busy) {
      return;
    }

    /*
     * If a new image is currently selected,
     * simply discard that replacement.
     */
    if (image) {
      setImage(null);

      return;
    }

    /*
     * Mark existing stored image for removal.
     */
    if (details?.admin?.imageUrl) {
      setRemoveExistingImage(true);

      setDetails((current) => ({
        ...current,

        admin: {
          ...current.admin,
          imageUrl: "",
        },
      }));
    }
  };

  // ==========================================================
  // UPLOAD PROFILE IMAGE
  // ==========================================================

  const uploadProfileImage = async () => {
    if (!image) {
      return "";
    }

    try {
      setUploadingPhoto(true);

      const result = await serviceUploadPhoto({
        file: image,

        folder: "fleetmaster/admin",

        filename: makeProfileFilename(details?.admin?.email, adminId),
      });

      if (!result?.url) {
        throw new Error("Profile image upload failed.");
      }

      return result.url;
    } finally {
      setUploadingPhoto(false);
    }
  };

  // ==========================================================
  // UPDATE PROFILE
  // ==========================================================

  const handleSubmit = async () => {
    if (loading || submitting || uploadingPhoto) {
      return;
    }

    if (!details?.admin) {
      return;
    }

    if (!isDirty) {
      toast.info("No changes to save.");

      return;
    }

    if (!normalize(details.admin.name)) {
      toast.error("Admin name is required.");

      return;
    }

    if (!isPhoneValid(details.admin.phone)) {
      toast.error("Phone number looks invalid.");

      return;
    }

    /*
     * If the image upload succeeds but the admin
     * update fails, this new image must be removed.
     */
    let uploadedImageUrl = "";

    let profileUpdated = false;

    try {
      setSubmitting(true);

      // ------------------------------------------------------
      // UPLOAD NEW IMAGE
      // ------------------------------------------------------

      if (image) {
        uploadedImageUrl = await uploadProfileImage();
      }

      // ------------------------------------------------------
      // BUILD UPDATE PAYLOAD todo ujj all payloads not compulsory
      // ------------------------------------------------------

      const payload = {
        /*
         * Email identifies the admin in your current
         * update API.
         */
        email: normalize(details.admin.email),

        name: normalize(details.admin.name),

        phone: normalize(details.admin.phone),
      };

      /*
       * Only include imageUrl when its state changed.
       */
      if (uploadedImageUrl) {
        payload.imageUrl = uploadedImageUrl;
      } else if (removeExistingImage) {
        payload.imageUrl = "";
      }

      // ------------------------------------------------------
      // UPDATE ADMIN
      // ------------------------------------------------------

      await serviceUpdateAdminDetails(payload);

      profileUpdated = true;

      // ------------------------------------------------------
      // DELETE OLD PHOTO
      // ------------------------------------------------------

      const imageChanged = uploadedImageUrl || removeExistingImage;

      const oldPhotoNeedsDeleting =
        originalImageUrl &&
        imageChanged &&
        originalImageUrl !== uploadedImageUrl;

      if (oldPhotoNeedsDeleting && isManagedPhoto(originalImageUrl)) {
        try {
          await serviceDeletePhoto(originalImageUrl);
        } catch (cleanupError) {
          console.error(
            "Failed deleting previous profile photo:",
            cleanupError,
          );

          /*
           * Profile update itself succeeded,
           * so do not report the entire update as failed.
           */
          toast.warn(
            "Profile updated, but the previous photo could not be cleaned up.",
          );
        }
      }

      toast.success("Profile details updated successfully.");

      navigate("/my-profile", {
        replace: true,
      });
    } catch (error) {
      console.error("Error updating profile:", error);

      /*
       * Upload succeeded, but admin update failed.
       * Delete orphaned new image.
       */
      if (uploadedImageUrl && !profileUpdated) {
        try {
          await serviceDeletePhoto(uploadedImageUrl);
        } catch (cleanupError) {
          console.error(
            "Failed cleaning up uploaded profile image:",
            cleanupError,
          );
        }
      }

      toast.error(error?.message || "Failed to update profile.");
    } finally {
      setSubmitting(false);
      setUploadingPhoto(false);
    }
  };

  // ==========================================================
  // CANCEL
  // ==========================================================

  const handleCancel = () => {
    if (busy) {
      return;
    }

    setImage(null);

    setRemoveExistingImage(false);

    navigate("/my-profile");
  };

  // ==========================================================
  // CURRENT DISPLAY IMAGE
  // ==========================================================

  const currentImageUrl =
    previewUrl ||
    (!removeExistingImage ? getImageUrl(details?.admin?.imageUrl) : "");

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="up-page">
      <header className="up-header">
        <button
          type="button"
          className="root-btn-back"
          onClick={handleCancel}
          disabled={busy}
          title="Back"
        >
          <i className="bi bi-chevron-left" aria-hidden="true" />
          Back
        </button>

        <div className="up-header-right">
          <div className="up-header-title">
            <div className="up-header-title-main">Update Profile</div>

            <div className="up-header-title-sub">
              {details?.admin?.email || "—"}
            </div>
          </div>

          <img src={logoImage} alt="Trucktronix" className="up-logo" />
        </div>
      </header>

      <main className="up-main">
        {loading ? (
          <div className="up-loading">
            <LoadingSpinner />

            <div className="up-loading-text">Loading profile…</div>
          </div>
        ) : details ? (
          <div className="up-card">
            <div className="up-card-topbar">
              <div className="up-chip">
                <i className="bi bi-person-badge" aria-hidden="true" />
                Admin Profile
              </div>

              <div className="up-topbar-right">
                {isDirty ? (
                  <div className="up-dirty">
                    <span className="up-dot" aria-hidden="true" />
                    Unsaved changes
                  </div>
                ) : (
                  <div className="up-dirty is-clean">
                    <i className="bi bi-check2" aria-hidden="true" />
                    Up to date
                  </div>
                )}
              </div>
            </div>

            {uploadingPhoto && (
              <div className="up-progress">
                <div className="up-progress-top">
                  <span>Uploading image…</span>
                </div>
              </div>
            )}

            <div className="up-content">
              {/* ==============================================
                  LEFT
              =============================================== */}

              <aside className="up-content-left">
                <div className="up-avatar-wrap">
                  <img
                    className="up-avatar"
                    src={currentImageUrl || iconEmployee}
                    alt={details?.admin?.name || "Profile"}
                  />

                  {(image || details?.admin?.imageUrl) && (
                    <button
                      type="button"
                      className="up-btn-delete"
                      onClick={handleRemovePhoto}
                      disabled={busy}
                    >
                      <i className="bi bi-trash" aria-hidden="true" />
                    </button>
                  )}
                </div>

                <div className="up-identity">
                  <div className="up-name">{details?.admin?.name || "—"}</div>

                  <div className="up-sub">
                    {details?.admin?.role || ""}
                  </div>
                </div>

                <div className="up-actions">
                  <label
                    className={`up-upload ${busy ? "is-disabled" : ""}`}
                    title="Upload profile image"
                  >
                    <BsCloudArrowUp className="up-upload-icon" />

                    <span>{currentImageUrl ? " Change" : " Upload"}</span>

                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChoose}
                      disabled={busy}
                    />
                  </label>

                  <button
                    type="button"
                    className="up-btn up-btn-primary"
                    onClick={handleSubmit}
                    disabled={!canSave}
                    title={!isDirty ? "Nothing to save" : undefined}
                  >
                    <i className="bi bi-check2-circle" aria-hidden="true" />

                    <span>
                      {uploadingPhoto
                        ? "Uploading…"
                        : submitting
                          ? "Saving…"
                          : "Save"}
                    </span>
                  </button>

                  <button
                    type="button"
                    className="up-btn up-btn-ghost"
                    onClick={handleCancel}
                    disabled={busy}
                  >
                    <i className="bi bi-x-circle" aria-hidden="true" />

                    <span>Cancel</span>
                  </button>
                </div>
              </aside>

              {/* ==============================================
                  RIGHT
              =============================================== */}

              <section className="up-content-right">
                <div className="up-grid">
                  {/* EMAIL */}

                  <div className="up-field up-field-wide">
                    <div className="up-label">Email</div>

                    <input
                      className="up-input"
                      disabled
                      value={details?.admin?.email || ""}
                    />
                  </div>

                  {/* ADMIN NAME */}

                  <div className="up-field">
                    <div className="up-label">Admin name</div>

                    <input
                      className="up-input"
                      value={details?.admin?.name || ""}
                      disabled={busy}
                      onChange={(event) =>
                        updateAdminField("name", event.target.value)
                      }
                    />
                  </div>

                  {/* PHONE */}

                  <div className="up-field">
                    <div className="up-label">Phone</div>

                    <input
                      className={`up-input ${
                        details?.admin?.phone &&
                        !isPhoneValid(details.admin.phone)
                          ? "is-invalid"
                          : ""
                      }`}
                      value={details?.admin?.phone || ""}
                      disabled={busy}
                      inputMode="tel"
                      onChange={(event) =>
                        updateAdminField("phone", event.target.value)
                      }
                      placeholder="e.g. 04xx xxx xxx"
                    />

                    {details?.admin?.phone &&
                      !isPhoneValid(details.admin.phone) && (
                        <div className="up-hint up-hint-error">
                          Enter a valid phone (8–15 digits).
                        </div>
                      )}
                  </div>

                  {/* ORGANISATION - DISPLAY ONLY */}

                  <div className="up-field up-field-wide">
                    <div className="up-label">Organisation</div>

                    <input
                      className="up-input"
                      value={details?.organization?.organizationName || ""}
                      disabled
                    />
                  </div>

                  {/* ADDRESS - DISPLAY ONLY */}

                  <div className="up-field up-field-wide">
                    <div className="up-label">Address</div>

                    <input
                      className="up-input"
                      value={details?.organization?.address || ""}
                      disabled
                    />
                  </div>
                </div>
              </section>
            </div>
          </div>
        ) : (
          <div className="up-empty">
            <div className="up-empty-icon">
              <i className="bi bi-exclamation-triangle" aria-hidden="true" />
            </div>

            <div className="up-empty-title">No data available</div>

            <div className="up-empty-sub">
              We couldn’t load your profile information.
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default UpdateProfile;
